import { useCallback, useMemo } from 'react';
import { apiCaller } from 'utils/axios';
import { API_ENDPOINTS } from '../apiEndpoints';
import Swal from 'sweetalert2';
import { useAcquittalsContext } from './context';
import { ACTIONS } from './reducer';

export const useAcquittalsData = () => {
    const [state, dispatch] = useAcquittalsContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const showErrorMessage = useCallback(
        (error) => {
            Swal.fire({
                title: 'Error',
                text: error.message || 'An unexpected error occurred',
                icon: 'error'
            });
            dispatch({ type: ACTIONS.SET_ERROR, payload: error });
        },
        [dispatch]
    );

    // Fetch acquittals with pagination and filters
    const fetchAcquittals = useCallback(
        async (params = {}) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();

                // Add filters from params
                if (params.acquittalStatus) queryParams.append('acquittalStatus', params.acquittalStatus);
                if (params.startDate) queryParams.append('startDate', params.startDate);
                if (params.endDate) queryParams.append('endDate', params.endDate);
                if (params.search) queryParams.append('search', params.search);

                // Add pagination
                if (params.page !== undefined) queryParams.append('page', params.page);
                if (params.size !== undefined) queryParams.append('size', params.size);

                // Add sorting
                if (params.sort && params.sort.length) {
                    params.sort.forEach((sortItem) => {
                        queryParams.append('sort', sortItem);
                    });
                }

                // Add pageable object if provided
                if (params.pageable) {
                    const pageableStr = JSON.stringify(params.pageable);
                    queryParams.append('pageable', pageableStr);
                }

                const endpoint = `${API_ENDPOINTS.TRAVEL_REQUESTS}/acquittals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
                const data = await apiCaller.get(endpoint);
                dispatch({ type: ACTIONS.SET_ACQUITTALS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    // Get acquittal by ID
    const getAcquittalById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}/acquittals/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_ACQUITTAL, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    // Export acquittals to CSV (client-side implementation)
    const exportAcquittalsToCSV = useCallback(
        (data) => {
            try {
                if (!data || data.length === 0) {
                    showErrorMessage({ message: 'No data to export' });
                    return;
                }

                // Define CSV headers
                const headers = [
                    'Request ID',
                    'Traveler Name',
                    'Trip Name',
                    'Departure Date',
                    'Return Date',
                    'Acquittal Due Date',
                    'Budget Amount',
                    'Status'
                ];

                // Convert data to CSV format
                const csvRows = data.map((row) => {
                    const travelerName = row.user ? `${row.user.firstName} ${row.user.lastName}` : 'Unknown';
                    const tripName = row.travelPlan ? row.travelPlan.planName : 'Unknown';
                    const departureDate = row.departureDatetime ? new Date(row.departureDatetime).toLocaleDateString() : '';
                    const returnDate = row.returnDatetime ? new Date(row.returnDatetime).toLocaleDateString() : '';
                    const budget = row.budgetAmount ? row.budgetAmount.toFixed(2) : '0.00';
                    // Format status for display
                    let status = 'Unknown';
                    if (row.latestAcquittalId === null) {
                        status = 'PENDING'; // Will be displayed as "Not Acquitted Yet" in the UI
                    } else {
                        status = row.acquittalStatus || row.status || 'Unknown';
                    }
                    const requestId = row.sequenceNumber || row.id;

                    // Calculate acquittal due date (return date + 3 days) with overdue status
                    let dueDate = '';
                    let isOverdue = false;
                    if (row.returnDatetime) {
                        const returnDate = new Date(row.returnDatetime);
                        const dueDateObj = new Date(returnDate);
                        dueDateObj.setDate(returnDate.getDate() + 3);
                        dueDate = dueDateObj.toLocaleDateString();

                        // Check if overdue
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        isOverdue = dueDateObj < today && row.latestAcquittalId === null;
                    }

                    // Add overdue indication to the CSV
                    const dueDateWithStatus = isOverdue ? `${dueDate} (OVERDUE)` : dueDate;

                    return [requestId, travelerName, tripName, departureDate, returnDate, dueDateWithStatus, budget, status].join(',');
                });

                // Add headers to CSV data
                csvRows.unshift(headers.join(','));

                // Create CSV content
                const csvContent = csvRows.join('\n');

                // Create a Blob and download
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', 'acquittals_report.csv');
                link.style.visibility = 'hidden';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showSuccessMessage('CSV exported successfully');
            } catch (error) {
                showErrorMessage(error.message ? error : { message: 'Failed to export CSV' });
            }
        },
        [showErrorMessage, showSuccessMessage]
    );

    return useMemo(
        () => ({
            // State
            acquittals: state.acquittals,
            loading: state.loading,
            error: state.error,
            selectedAcquittal: state.selectedAcquittal,

            // Operations
            fetchAcquittals,
            getAcquittalById,
            exportAcquittalsToCSV
        }),
        [state, fetchAcquittals, getAcquittalById, exportAcquittalsToCSV]
    );
};

export default useAcquittalsData;
